/**
 * Ablage beim Versenden (Kundenfeedback Kanzlei Polz 24.10.2026, IV.2):
 * „Wenn man Dokumente per Mail versendet, wäre es gut, wenn man auswählen
 * könnte, ob und wo die Dokumente mit gespeichert werden."
 *
 * Im E-Mail-Fenster wählt der Absender: nicht ablegen, oder in einen Ordner
 * der Akte – die E-Mail als PDF und/oder die Anhänge. Vorbelegt wird mit der
 * Einstellung der Organisation (automatische Ablage gesendeter E-Mails).
 * Sobald eine Auswahl feststeht, schickt das Fenster skipAutoFile mit; die
 * Organisationseinstellung legt dann nichts zusätzlich ab (Migration 0330).
 */
import type { FolderTargets } from '../chat/caseFolders'
import { gt } from '@/i18n/gt'
import { targetLabel } from '../chat/caseFolders'
import { fileMessageAttachments, fileMessageToCase, resolveFolderValue } from '../chat/emailFilingApi'

/** 'auto' = Einstellung unbekannt (nicht geladen) → Organisation entscheidet. */
export type FilingMode = 'auto' | 'none' | 'folder'

export interface FilingChoice {
  mode: FilingMode
  /** Wert aus der Ordnerwahl (Kategorie, "folder:<id>" oder Vorlage). */
  folder: string
  withMail: boolean
  withAttachments: boolean
}

export type FilingAction = 'none' | 'mail' | 'mail+attachments' | 'attachments'

export interface FilingPlan {
  /** Organisationseinstellung übergehen (der Absender hat entschieden). */
  skipAutoFile: boolean
  action: FilingAction
}

export function initialFilingChoice (autoFileSentEmails: boolean | null, defaultFolder: string): FilingChoice {
  if (autoFileSentEmails === null) {
    return { mode: 'auto', folder: defaultFolder, withMail: true, withAttachments: true }
  }
  return {
    mode: autoFileSentEmails && defaultFolder ? 'folder' : 'none',
    folder: defaultFolder,
    withMail: true,
    withAttachments: true,
  }
}

/** Was nach dem Versand geschieht. */
export function filingPlan (choice: FilingChoice, attachmentCount: number): FilingPlan {
  if (choice.mode === 'auto') {
    return { skipAutoFile: false, action: 'none' }
  }
  if (choice.mode === 'none' || !choice.folder) {
    return { skipAutoFile: true, action: 'none' }
  }
  const attachments = choice.withAttachments && attachmentCount > 0
  if (choice.withMail) {
    return { skipAutoFile: true, action: attachments ? 'mail+attachments' : 'mail' }
  }
  return { skipAutoFile: true, action: attachments ? 'attachments' : 'none' }
}

/**
 * Kurztext für den Knopf im E-Mail-Fenster. Kundenfeedback 26.10.2026, VII:
 * „Nicht in der Akte ablegen“ klang, als würde die E-Mail gar nicht
 * gespeichert. Sie steht immer in der Korrespondenz der Akte – der Knopf
 * sagt nur, ob sie ZUSÄTZLICH in einen Dokumentenordner kommt.
 */
export function filingSummary (choice: FilingChoice, targets: FolderTargets | null, attachmentCount: number): string {
  if (choice.mode === 'auto') {
    return gt('In Ordner speichern: wie eingestellt')
  }
  const plan = filingPlan(choice, attachmentCount)
  if (plan.action === 'none') {
    return gt('Nicht zusätzlich in Ordner speichern')
  }
  const folder = targetLabel(targets, choice.folder)
  return plan.action === 'attachments'
    ? `${gt('Anhänge in Ordner speichern')}: ${folder}`
    : `${gt('In Ordner speichern')}: ${folder}`
}

/** Erklärung beim Darüberfahren: was nach dem Senden genau passiert. */
export function filingTooltip (choice: FilingChoice, targets: FolderTargets | null, attachmentCount: number): string {
  const base = gt('Die E-Mail steht nach dem Senden immer in der Korrespondenz der Akte.')
  if (choice.mode === 'auto') {
    return `${base} ${gt('Ob sie zusätzlich in einem Ordner gespeichert wird, richtet sich nach der Einstellung Ihrer Organisation.')}`
  }
  const plan = filingPlan(choice, attachmentCount)
  const folder = `„${targetLabel(targets, choice.folder)}“`
  switch (plan.action) {
    case 'mail': {
      return `${base} ${gt('Zusätzlich wird sie als PDF im Ordner {folder} gespeichert.').replace('{folder}', folder)}`
    }
    case 'mail+attachments': {
      return `${base} ${gt('Zusätzlich werden die E-Mail als PDF und ihre Anhänge im Ordner {folder} gespeichert.').replace('{folder}', folder)}`
    }
    case 'attachments': {
      return `${base} ${gt('Zusätzlich werden ihre Anhänge im Ordner {folder} gespeichert.').replace('{folder}', folder)}`
    }
    default: {
      return `${base} ${gt('Sie wird nicht zusätzlich in einem Ordner gespeichert.')}`
    }
  }
}

/**
 * Ablage nach dem Versand ausführen. Liefert die Erfolgsmeldung oder wirft
 * mit einer verständlichen Meldung.
 */
export async function fileSentMessage (opts: {
  caseId: number
  messageId: number
  attachmentIds: number[]
  choice: FilingChoice
  targets: FolderTargets | null
}): Promise<string | null> {
  const plan = filingPlan(opts.choice, opts.attachmentIds.length)
  if (plan.action === 'none') {
    return null
  }
  const category = opts.targets
    ? await resolveFolderValue(opts.caseId, opts.targets, opts.choice.folder)
    : opts.choice.folder
  const folder = targetLabel(opts.targets, opts.choice.folder)
  if (plan.action === 'attachments') {
    const docs = await fileMessageAttachments(opts.messageId, opts.attachmentIds.map(idAttachment => ({ idAttachment, category })))
    return gt('{n} Anhänge abgelegt: Dokumente › {folder}').replace('{n}', String(docs.length)).replace('{folder}', folder)
  }
  const res = await fileMessageToCase(opts.messageId, category, plan.action === 'mail+attachments')
  return gt('E-Mail abgelegt: Dokumente › {folder}').replace('{folder}', folder)
    + (res.attachmentDocumentIds.length > 0 ? ` (+${res.attachmentDocumentIds.length} ${gt('Anhänge')})` : '')
}
